import { useEffect, useState, type FC } from "react";
import { useClientManualRules } from "../../../graphql/hooks/aml/useClientManualRules";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import Alert from "../Alert";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { theme } from "../../../theme";
import { Row } from "react-grid-system";
import {
  ChevronSmallDownIcon,
  ChevronSmallUpIcon,
} from "../../atoms/icons/ChevronIcons";
import { SlideOutHeight } from "../../atoms/SlideOutHeight";
import Body from "../../atoms/typography/Body";
import { getRiskScoreTitleAndDescription } from "../../../utils/amlHelpers";
import { HorizontalSpacer } from "../../atoms/Spacers";
import BodySmall from "../../atoms/typography/BodySmall";
import Checkbox from "../../atoms/inputs/Checkbox";
import { useUpdateManualRule } from "../../../graphql/hooks/aml/useUpdateManualRule";
import SmallLoadingSpinner from "../../atoms/SmallLoadingSpinner";
import Subheading from "../../atoms/typography/Subheading";

interface Props {
  clientIdentifier: string;
}

const Container = styled.div`
  border: 1px solid ${theme.colors.grey05};
  border-radius: 12px;
  background-color: ${theme.colors.grey02};
`;

const HeaderRow = styled(Row)`
  padding: 16px;

  &:hover {
    cursor: pointer;
    background-color: ${theme.colors.grey05};
    border-radius: 12px;
  }
`;

const ContentContainer = styled.div`
  padding: 16px;
`;

const RulesWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
`;

const ClientManualRules: FC<Props> = ({ clientIdentifier }) => {
  const { t } = useTranslation();
  const { rules, error, loading } = useClientManualRules(clientIdentifier);
  const [open, setOpen] = useState(false);
  const [successfulUpdate, setSuccessfulUpdate] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [checkedCheckboxes, setCheckedCheckboxes] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    if (!rules) return;
    const newState: Record<string, boolean> = {};

    rules.forEach((rule) => {
      rule.selectableValues?.forEach((v) => {
        newState[`${rule.ruleType}-${v.title}`] = v.value;
      });
    });

    setCheckedCheckboxes(newState);
  }, [rules]);

  const onCompletedUpdateManualRule = (result?: any) => {
    if (result?.aml?.updateAmlManualRule?.success) {
      setSuccessfulUpdate(true);
      setErrorMessage("");
      setTimeout(() => {
        setSuccessfulUpdate(false);
      }, 3000);
    } else {
      setErrorMessage(t("aml.unableToSaveEdits"));
    }
  };
  const onErrorUpdateManualRule = (e: any) => {
    setErrorMessage(t("aml.unableToSaveEdits" + ": " + e?.toString()));
  };
  const {
    updateManualRule,
    error: updateError,
    loading: updateLoading,
  } = useUpdateManualRule({
    onCompleted: onCompletedUpdateManualRule,
    onError: onErrorUpdateManualRule,
  });

  const onClickSave = async (ruleType: string, value: boolean) => {
    try {
      updateManualRule({
        variables: {
          input: {
            clientIdentifier: clientIdentifier,
            ruleType: ruleType,
            value: value,
          },
        },
      });
    } catch (error) {
      console.log("error", error);
      setErrorMessage(t("aml.unableToSaveEdits"));
    }
  };

  const onChangeCheckbox = (ruleType: string, valueTitle: string) => {
    const key = `${ruleType}-${valueTitle}`;
    const newValue = !checkedCheckboxes[key];
    setCheckedCheckboxes((prev) => ({
      ...prev,
      [key]: newValue,
    }));

    onClickSave(ruleType, newValue);
  };

  return (
    <div>
      {loading && <LoadingSpinner />}
      {error && (
        <Alert
          type="error"
          title={t("errors.somethingWentWrong")}
          message={error?.toString()}
        />
      )}
      <Container>
        <HeaderRow
          nogutter
          justify="between"
          align="center"
          onClick={() => setOpen(!open)}
        >
          <Subheading>{t("aml.manualRules")}</Subheading>
          {open ? <ChevronSmallUpIcon /> : <ChevronSmallDownIcon />}
        </HeaderRow>
        <SlideOutHeight isVisible={open}>
          <ContentContainer>
            <RulesWrapper>
              {rules?.map((rule) => (
                <div key={rule.ruleType}>
                  <Body>
                    {t(getRiskScoreTitleAndDescription(rule.ruleType).title)}
                  </Body>
                  <HorizontalSpacer spacing={0.5} />
                  <BodySmall>
                    {t(
                      getRiskScoreTitleAndDescription(rule.ruleType)
                        .description,
                    )}
                  </BodySmall>

                  <HorizontalSpacer spacing={0.5} />
                  {rule.selectableValues?.map((value) => {
                    const key = `${rule.ruleType}-${value.title}`;

                    return (
                      <div key={key}>
                        <Checkbox
                          title={value.title}
                          checked={checkedCheckboxes[key]}
                          onChange={() =>
                            onChangeCheckbox(rule.ruleType, value.title)
                          }
                          disabled={!rule.isEditable || updateLoading}
                        />
                      </div>
                    );
                  })}
                </div>
              ))}
            </RulesWrapper>
            <Row justify="end" nogutter>
              {successfulUpdate && (
                <BodySmall color={theme.colors.success}>
                  {t("aml.editsHaveBeenSaved")}
                </BodySmall>
              )}
              {(errorMessage || updateError) && (
                <BodySmall color={theme.colors.error}>
                  {errorMessage || updateError?.toString()}
                </BodySmall>
              )}
              {updateLoading && <SmallLoadingSpinner />}
            </Row>
          </ContentContainer>
        </SlideOutHeight>
      </Container>
    </div>
  );
};

export default ClientManualRules;
